// Outlook .msg parser — extracts the email body + attachments out of the
// proprietary Compound File Binary Format so the rest of the app can treat
// them like any other email + file set.
//
// Used by Module 2 paperless intake and Module 5 RFQ intake. .msg files
// arrive whenever an RFQ is forwarded directly from Outlook by drag-drop.
// Without this, Claude sees a binary blob it can't read.

import MsgReader from '@kenjiuno/msgreader';

export interface ExpandedAttachment {
  name: string;
  mimeType: string;
  data: string; // base64
  sizeBytes: number;
}

export interface ExpandedMsg {
  email: {
    sender: string;
    senderEmail: string;
    subject: string;
    sentDate: string;
    body: string;
  };
  attachments: ExpandedAttachment[];
}

export function isMsgFile(file: File): boolean {
  return file.name.toLowerCase().endsWith('.msg');
}

const EXT_TO_MIME: Record<string, string> = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  bmp: 'image/bmp',
  tif: 'image/tiff',
  tiff: 'image/tiff',
  dxf: 'application/dxf',
  dwg: 'application/dwg',
  step: 'application/step',
  stp: 'application/step',
  iges: 'application/iges',
  igs: 'application/iges',
  sldprt: 'application/sldprt',
  stl: 'model/stl',
  txt: 'text/plain',
  csv: 'text/csv',
  json: 'application/json',
  xml: 'application/xml',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

function guessMimeFromName(name: string): string {
  const ext = (name.split('.').pop() ?? '').toLowerCase();
  return EXT_TO_MIME[ext] ?? 'application/octet-stream';
}

function uint8ToBase64(bytes: Uint8Array): string {
  // Chunked conversion — avoids "Maximum call stack size exceeded" on large attachments
  const chunkSize = 0x8000; // 32KB chunks
  let binary = '';
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, Math.min(i + chunkSize, bytes.length));
    binary += String.fromCharCode.apply(null, Array.from(chunk));
  }
  return btoa(binary);
}

interface MsgFileData {
  subject?: string;
  senderName?: string;
  senderEmail?: string;
  body?: string;
  bodyHTML?: string;
  messageDeliveryTime?: string;
  clientSubmitTime?: string;
  creationTime?: string;
  attachments?: Array<{
    fileName?: string;
    displayName?: string;
    mimeType?: string;
  }>;
  error?: string;
}

interface MsgAttachmentContent {
  fileName?: string;
  content?: Uint8Array;
}

function pickSentDate(d: MsgFileData): string {
  return d.messageDeliveryTime || d.clientSubmitTime || d.creationTime || '';
}

function stripHtml(html: string): string {
  // crude but adequate fallback when only HTML body is present
  return html
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * Parse an Outlook .msg file into email metadata + flat attachment list.
 * Throws if the file isn't a valid .msg.
 */
export async function expandMsgFile(file: File): Promise<ExpandedMsg> {
  const buffer = await file.arrayBuffer();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const reader = new MsgReader(buffer) as any;
  const data = reader.getFileData() as MsgFileData;

  if (data.error) {
    throw new Error(`Failed to parse .msg: ${data.error}`);
  }

  const sender = data.senderName ?? '';
  const senderEmail = data.senderEmail ?? '';
  const subject = data.subject ?? '';
  const sentDate = pickSentDate(data);
  // Prefer plain text; fall back to stripped HTML
  const body = data.body && data.body.trim().length > 0
    ? data.body
    : data.bodyHTML
      ? stripHtml(data.bodyHTML)
      : '';

  const attachments: ExpandedAttachment[] = [];
  const attMetas = data.attachments ?? [];
  for (let i = 0; i < attMetas.length; i++) {
    const meta = attMetas[i];
    let content: MsgAttachmentContent;
    try {
      content = reader.getAttachment(i) as MsgAttachmentContent;
    } catch (e) {
      // Skip this attachment if we can't pull bytes
      console.warn(`Failed to extract attachment ${i} from .msg:`, e);
      continue;
    }
    if (!content?.content) continue;

    const name = content.fileName ?? meta.fileName ?? meta.displayName ?? `attachment-${i}`;
    const mimeType = meta.mimeType ?? guessMimeFromName(name);
    const bytes = content.content;
    attachments.push({
      name,
      mimeType,
      data: uint8ToBase64(bytes),
      sizeBytes: bytes.length,
    });
  }

  return {
    email: { sender, senderEmail, subject, sentDate, body },
    attachments,
  };
}
